<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="balance-scale"/> {{ $t('pageTitle') }}</h1>
      </div>
    </div>

    <div class="row">
      <!-- Bộ lọc -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <label class="mr-1 mb-1">{{ $t('period') }}</label>
            <date-picker v-model="period" type="month" value-type="YYYY-MM" format="MM/YYYY"
                         :lang="$i18n.locale" :clearable="false" :placeholder="$t('selectMonth')"
                         class="mr-2 mb-1"></date-picker>
            <label class="mr-1 mb-1">{{ $t('provider') }}</label>
            <b-form-select class="mr-2 mb-1" v-model="gateway" :options="gatewayOptions" style="width: 200px"></b-form-select>
            <button @click="loadPivot" class="btn btn-primary mr-1 mb-1" :disabled="loading">
              <font-awesome-icon icon="sync" :spin="loading"/>&nbsp;{{ $t('loadReport') }}
            </button>
            <label class="btn btn-outline-success is-border-1 mr-1 mb-1" :class="{disabled: importing}">
              <font-awesome-icon icon="file-upload"/>&nbsp;{{ $t('importProviderFile') }}
              <input type="file" accept=".xlsx" hidden @change="onFile" :disabled="importing"/>
            </label>
            <button v-if="saved" @click="exportExcel" class="btn btn-outline-info is-border-1 mr-1 mb-1" :disabled="exporting">
              <font-awesome-icon icon="file-excel"/>&nbsp;{{ $t('export') }}
            </button>
          </div>
          <b-alert show variant="info" class="small mt-2 mb-0">{{ $t('reconcileNote') }}</b-alert>
        </div>
      </div>

      <!-- Trạng thái + thông tin import/chốt -->
      <div class="col-12" v-if="saved">
        <div class="tile d-flex flex-wrap align-items-center" style="gap:.5rem">
          <span class="badge" :class="saved.status === 'confirmed' ? 'badge-success' : 'badge-warning'">
            {{ saved.status === 'confirmed' ? $t('confirmed') : $t('draft') }}
          </span>
          <span class="small text-gray-999" v-if="saved.importedBy">
            {{ $t('importedBy') }}: <b>{{ saved.importedBy.name }}</b>
            <span v-if="saved.updateTime">— {{ saved.updateTime | moment('DD/MM/YYYY HH:mm') }}</span>
          </span>
          <span class="small text-success" v-if="saved.status === 'confirmed' && saved.confirmedBy">
            <font-awesome-icon icon="check-circle"/> {{ $t('confirmedBy') }}: <b>{{ saved.confirmedBy.name }}</b>
            <span v-if="saved.confirmTime">— {{ saved.confirmTime | moment('DD/MM/YYYY HH:mm') }}</span>
          </span>
          <span class="ml-auto">
            <button v-if="saved.status !== 'confirmed'" @click="deleteReconcile(saved._id)" class="btn btn-sm btn-outline-danger is-border-1 mr-1" :disabled="deleting || confirming">
              <font-awesome-icon icon="trash"/>&nbsp;{{ $t('deleteBtn') }}
            </button>
            <button v-if="saved.status !== 'confirmed'" @click="confirm(true)" class="btn btn-sm btn-success" :disabled="confirming">
              <font-awesome-icon icon="check"/>&nbsp;{{ $t('confirmBtn') }}
            </button>
            <button v-else @click="confirm(false)" class="btn btn-sm btn-outline-secondary is-border-1" :disabled="confirming">
              {{ $t('unconfirmBtn') }}
            </button>
          </span>
        </div>
      </div>

      <!-- Băng tổng hợp -->
      <div class="col-12" v-if="saved">
        <div class="row">
          <div class="col-md-3 col-6">
            <div class="widget-small primary tile"><div class="info">
              <h4>{{ $t('systemRevenue') }}</h4><p><b>{{ totalRevenue | numFormat('0,0') }}đ</b></p>
              <small>{{ $t('msgRevenueShort') }} {{ saved.systemTotals.revenue | numFormat('0,0') }}đ ·
                {{ $t('subscriptionRevenueShort') }} {{ subscriptionRevenue | numFormat('0,0') }}đ</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small warning tile"><div class="info">
              <h4>{{ $t('providerGross') }}</h4><p><b>{{ saved.providerTotals.grossAmount | numFormat('0,0') }}đ</b></p>
              <small>{{ $t('withVatShort') }} {{ saved.providerTotals.payable | numFormat('0,0') }}đ</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small tile" :class="marginBeforeVat >= 0 ? 'info' : 'danger'"><div class="info">
              <h4>{{ $t('grossMargin') }}</h4><p><b>{{ marginBeforeVat | numFormat('0,0') }}đ</b></p>
              <small>{{ $t('marginAfterVatShort') }} {{ marginAfterVat | numFormat('0,0') }}đ</small>
            </div></div>
          </div>
          <div class="col-md-3 col-6">
            <div class="widget-small tile" :class="totalDiffCount === 0 ? 'info' : 'danger'"><div class="info">
              <h4>{{ $t('qtyDiff') }}</h4><p><b>{{ totalDiffCount | numFormat('+0,0') }}</b></p>
              <small>{{ totalDiffCount === 0 ? $t('matched') : $t('needReview') }}</small>
            </div></div>
          </div>
        </div>
      </div>

      <!-- Đối soát TIỀN (khớp hoá đơn NCC) -->
      <div class="col-lg-5 col-12" v-if="saved">
        <div class="tile">
          <h5 class="mb-2">{{ $t('moneyRecon') }}</h5>
          <table class="table table-sm mb-0">
            <tbody>
              <tr><td>{{ $t('usageAmount') }}</td><td class="text-right">{{ saved.providerTotals.usageAmount | numFormat('0,0') }}đ</td></tr>
              <tr>
                <td>{{ $t('maintainAmount') }}
                  <span class="text-gray-999 small" v-if="saved.providerTotals.maintainCount">({{ saved.providerTotals.maintainCount | numFormat('0,0') }} {{ $t('brandMonths') }})</span>
                </td>
                <td class="text-right">{{ saved.providerTotals.maintainAmount | numFormat('0,0') }}đ</td>
              </tr>
              <tr class="font-weight-bold"><td>{{ $t('grossAmount') }}</td><td class="text-right">{{ saved.providerTotals.grossAmount | numFormat('0,0') }}đ</td></tr>
              <tr><td>{{ $t('vat') }}</td><td class="text-right">+ {{ saved.providerTotals.vat | numFormat('0,0') }}đ</td></tr>
              <tr class="font-weight-bold"><td>{{ $t('providerPayable') }}</td><td class="text-right">{{ saved.providerTotals.payable | numFormat('0,0') }}đ</td></tr>
              <tr><td class="text-primary">{{ $t('msgRevenue') }}</td><td class="text-right text-primary">{{ saved.systemTotals.revenue | numFormat('0,0') }}đ</td></tr>
              <tr>
                <td class="text-primary">{{ $t('subscriptionRevenue') }}
                  <span class="text-gray-999 small" v-if="saved.systemTotals.subscriptionCount">({{ saved.systemTotals.subscriptionCount | numFormat('0,0') }} {{ $t('brandUnit') }})</span>
                </td>
                <td class="text-right text-primary">{{ subscriptionRevenue | numFormat('0,0') }}đ</td>
              </tr>
              <tr class="font-weight-bold"><td class="text-primary">{{ $t('totalRevenue') }}</td><td class="text-right text-primary">{{ totalRevenue | numFormat('0,0') }}đ</td></tr>
              <tr class="font-weight-bold" :class="marginBeforeVat >= 0 ? 'text-success' : 'text-danger'">
                <td>{{ $t('marginBeforeVat') }}</td><td class="text-right">{{ marginBeforeVat | numFormat('0,0') }}đ</td>
              </tr>
              <tr :class="marginAfterVat >= 0 ? 'text-success' : 'text-danger'">
                <td>{{ $t('marginAfterVat') }}</td><td class="text-right">{{ marginAfterVat | numFormat('0,0') }}đ</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Context giải thích lệch -->
        <b-alert show variant="light" class="small border" v-if="saved.systemTotals.sentTotal">
          <b>{{ $t('contextTitle') }}:</b>
          {{ $t('ctxSent') }} <b>{{ saved.systemTotals.sentTotal | numFormat('0,0') }}</b>
          <span v-if="saved.systemTotals.sentDocCount" class="text-gray-999">({{ saved.systemTotals.sentDocCount | numFormat('0,0') }} {{ $t('msgUnit') }})</span> ·
          {{ $t('ctxCharged') }} <b>{{ saved.systemTotals.billableCount | numFormat('0,0') }}</b> ·
          {{ $t('ctxFail') }} <b>{{ saved.systemTotals.failCount | numFormat('0,0') }}</b>
          <span v-if="saved.systemTotals.failDocCount" class="text-gray-999">({{ saved.systemTotals.failDocCount | numFormat('0,0') }} {{ $t('msgUnit') }})</span>
          <div class="text-gray-999 mt-1">{{ $t('ctxUnitNote') }}</div>
          <div class="text-gray-999 mt-1">{{ $t('diffLegend') }}</div>
        </b-alert>
        <b-alert show variant="warning" class="small" v-if="saved.providerTotals.maintainAmount">{{ $t('maintainNote') }}</b-alert>
        <b-alert show variant="warning" class="small" v-if="saved.liveBackfilled">{{ $t('liveBackfillNote') }}</b-alert>
      </div>

      <!-- Bảng đối soát SẢN LƯỢNG theo brand -->
      <div class="col-lg-7 col-12" v-if="saved">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-4">{{ $t('brand') }}</div>
            <div class="col-lg-2 text-right">{{ $t('systemCount') }}</div>
            <div class="col-lg-2 text-right">{{ $t('providerCount') }}</div>
            <div class="col-lg-1 text-right">{{ $t('diff') }}</div>
            <div class="col-lg-3 text-right">{{ $t('providerAmount') }}</div>
          </div>
          <div class="row" v-for="(l, i) in saved.lines" :key="i" :class="{'bg-danger-soft': l.diffCount !== 0}">
            <div class="col-lg-4">
              <span>{{ l.brandName }}</span>
              <span v-if="l.source === 'providerOnly'" class="badge badge-warning ml-1">{{ $t('providerOnly') }}</span>
              <span v-if="l.source === 'systemOnly'" class="badge badge-info ml-1">{{ $t('systemOnly') }}</span>
            </div>
            <div class="col-lg-2 text-right">{{ l.systemCount | numFormat('0,0') }}</div>
            <div class="col-lg-2 text-right">{{ l.providerCount | numFormat('0,0') }}</div>
            <div class="col-lg-1 text-right" :class="l.diffCount === 0 ? 'text-success' : 'text-danger font-weight-bold'">
              {{ l.diffCount | numFormat('+0,0') }}
            </div>
            <div class="col-lg-3 text-right">
              {{ l.providerAmount | numFormat('0,0') }}
              <button class="btn btn-sm btn-link p-0 ml-1" v-if="l.systemCount" @click="drill(l)" :title="$t('viewDetail')">
                <font-awesome-icon icon="search"/>
              </button>
            </div>
          </div>
          <div class="row c-smart-table__title">
            <div class="col-lg-4"><b>{{ $t('total') }}</b></div>
            <div class="col-lg-2 text-right"><b>{{ savedTotals.systemCount | numFormat('0,0') }}</b></div>
            <div class="col-lg-2 text-right"><b>{{ savedTotals.providerCount | numFormat('0,0') }}</b></div>
            <div class="col-lg-1 text-right" :class="savedTotals.diffCount === 0 ? 'text-success' : 'text-danger'"><b>{{ savedTotals.diffCount | numFormat('+0,0') }}</b></div>
            <div class="col-lg-3 text-right"><b>{{ savedTotals.providerAmount | numFormat('0,0') }}đ</b></div>
          </div>
        </div>
      </div>

      <!-- Đối soát phí duy trì brand name: NCC tính theo NHÀ MẠNG ↔ Callio thu theo BRAND.
           Hai phía khác chiều nên đặt song song, chỉ khớp ở tổng tiền. -->
      <div class="col-12" v-if="saved && (maintainLines.length || subscriptionLines.length)">
        <div class="tile">
          <h5 class="mb-2">{{ $t('maintainRecon') }}</h5>
          <div class="row">
            <div class="col-md-6">
              <div class="small text-gray-999 mb-1">{{ $t('maintainProviderSide') }}</div>
              <table class="table table-sm mb-2">
                <thead><tr>
                  <th>{{ $t('carrier') }}</th>
                  <th class="text-right">{{ $t('brandMonths') }}</th>
                  <th class="text-right">{{ $t('amount') }}</th>
                </tr></thead>
                <tbody>
                  <tr v-for="(l, i) in maintainLines" :key="i">
                    <td>{{ l.carrier }}</td>
                    <td class="text-right">{{ l.count | numFormat('0,0') }}</td>
                    <td class="text-right">{{ l.amount | numFormat('0,0') }}đ</td>
                  </tr>
                  <tr v-if="!maintainLines.length">
                    <td colspan="3" class="text-gray-999">{{ $t('maintainNoDetail') }}</td>
                  </tr>
                  <tr class="font-weight-bold">
                    <td>{{ $t('total') }}</td>
                    <td class="text-right">{{ (maintainLines.length ? maintainTotals.count : saved.providerTotals.maintainCount) | numFormat('0,0') }}</td>
                    <td class="text-right">{{ (maintainLines.length ? maintainTotals.amount : saved.providerTotals.maintainAmount) | numFormat('0,0') }}đ</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="col-md-6">
              <div class="small text-gray-999 mb-1">{{ $t('maintainSystemSide') }}</div>
              <table class="table table-sm mb-2">
                <thead><tr>
                  <th>{{ $t('brand') }}</th>
                  <th>{{ $t('client') }}</th>
                  <th class="text-right">{{ $t('amount') }}</th>
                </tr></thead>
                <tbody>
                  <tr v-for="(l, i) in subscriptionLines" :key="i">
                    <td>{{ l.brandName }}</td>
                    <td class="text-truncate" style="max-width: 220px">{{ l.clientName }}</td>
                    <td class="text-right">{{ l.amount | numFormat('0,0') }}đ</td>
                  </tr>
                  <tr v-if="!subscriptionLines.length">
                    <td colspan="3" class="text-danger">{{ $t('noSubscription') }}</td>
                  </tr>
                  <tr class="font-weight-bold">
                    <td colspan="2">{{ $t('total') }}</td>
                    <td class="text-right">{{ subscriptionRevenue | numFormat('0,0') }}đ</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="font-weight-bold" :class="maintainMargin >= 0 ? 'text-success' : 'text-danger'">
            {{ $t('maintainMargin') }}: {{ maintainMargin | numFormat('0,0') }}đ
          </div>
          <div class="small text-gray-999 mt-1">{{ $t('maintainReconNote') }}</div>

          <!-- Brand đang duy trì mà kỳ này không thu được đồng nào. expectedFee > 0 = thất thu thật
               (thường do khách hết credit); = 0 là brand được miễn phí, không phải lỗi. -->
          <div v-if="unbilledBrands.length" class="mt-3">
            <div class="small font-weight-bold mb-1">
              {{ $t('unbilledBrands') }}
              <span class="badge ml-1" :class="unbilledLeak.length ? 'badge-danger' : 'badge-secondary'">{{ unbilledBrands.length }}</span>
              <span v-if="unbilledLeakAmount" class="text-danger ml-2">{{ $t('unbilledLeak') }}: {{ unbilledLeakAmount | numFormat('0,0') }}đ/{{ $t('monthUnit') }}</span>
            </div>
            <table class="table table-sm mb-0">
              <tbody>
                <tr v-for="(b, i) in unbilledBrands" :key="i" :class="{'bg-danger-soft': b.expectedFee > 0}">
                  <td>{{ b.brandName }}</td>
                  <td class="text-truncate" style="max-width: 260px">{{ b.clientName }}</td>
                  <td class="text-right" :class="b.expectedFee > 0 ? 'text-danger font-weight-bold' : 'text-gray-999'">
                    <span v-if="b.expectedFee > 0">{{ b.expectedFee | numFormat('0,0') }}đ</span>
                    <span v-else>{{ $t('freeOfCharge') }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pivot tự sinh từ DB (chưa import file) -->
      <div class="col-12" v-if="!saved && pivotLines.length">
        <b-alert show variant="secondary" class="small">
          {{ $t('pivotOnlyNote') }}
          <span v-if="pivotTotals.sentTotal"> — {{ $t('ctxSent') }} <b>{{ pivotTotals.sentTotal | numFormat('0,0') }}</b>,
            {{ $t('ctxCharged') }} <b>{{ pivotTotals.billableCount | numFormat('0,0') }}</b>,
            {{ $t('ctxFail') }} <b>{{ pivotTotals.failCount | numFormat('0,0') }}</b></span>
          <span v-if="pivotTotals.subscriptionRevenue"> — {{ $t('subscriptionRevenue') }}
            <b>{{ pivotTotals.subscriptionRevenue | numFormat('0,0') }}đ</b>
            ({{ pivotTotals.subscriptionCount | numFormat('0,0') }} {{ $t('brandUnit') }})</span>
        </b-alert>
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-6">{{ $t('brand') }}</div>
            <div class="col-lg-3 text-right">{{ $t('systemCount') }}</div>
            <div class="col-lg-3 text-right">{{ $t('systemRevenueShort') }}</div>
          </div>
          <div class="row" v-for="(l, i) in pivotLines" :key="i">
            <div class="col-lg-6">{{ l.brandName }}</div>
            <div class="col-lg-3 text-right">{{ l.systemCount | numFormat('0,0') }}</div>
            <div class="col-lg-3 text-right">{{ l.systemRevenue | numFormat('0,0') }}đ</div>
          </div>
          <div class="row c-smart-table__title">
            <div class="col-lg-6"><b>{{ $t('total') }}</b></div>
            <div class="col-lg-3 text-right"><b>{{ pivotTotals.billableCount | numFormat('0,0') }}</b></div>
            <div class="col-lg-3 text-right"><b>{{ pivotTotals.revenue | numFormat('0,0') }}đ</b></div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-danger" v-if="!saved && !pivotLines.length && loaded">{{ $t('noData') }}</div>

      <!-- Lịch sử đối soát -->
      <div class="col-12" v-if="history.length">
        <div class="tile">
          <h5 class="mb-2">{{ $t('history') }}</h5>
          <table class="table table-sm table-hover mb-0">
            <thead><tr>
              <th>{{ $t('period') }}</th><th>{{ $t('provider') }}</th>
              <th class="text-right">{{ $t('providerPayable') }}</th>
              <th class="text-right">{{ $t('systemRevenue') }}</th>
              <th class="text-right">{{ $t('marginBeforeVat') }}</th>
              <th class="text-right">{{ $t('qtyDiff') }}</th>
              <th>{{ $t('status') }}</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="h in history" :key="h._id" style="cursor:pointer" @click="openHistory(h)">
                <td><b>{{ formatPeriod(h.period) }}</b></td>
                <td>{{ h.gateway ? h.gateway.name : $t('allProviders') }}</td>
                <td class="text-right">{{ h.providerTotals.payable | numFormat('0,0') }}đ</td>
                <td class="text-right">{{ (h.systemTotals.revenue + (h.systemTotals.subscriptionRevenue || 0)) | numFormat('0,0') }}đ</td>
                <td class="text-right" :class="historyMargin(h) >= 0 ? 'text-success' : 'text-danger'">
                  {{ historyMargin(h) | numFormat('0,0') }}đ
                </td>
                <td class="text-right" :class="(h.systemTotals.billableCount - h.providerTotals.usageCount) === 0 ? 'text-success' : 'text-danger'">
                  {{ (h.systemTotals.billableCount - h.providerTotals.usageCount) | numFormat('+0,0') }}
                </td>
                <td><span class="badge" :class="h.status === 'confirmed' ? 'badge-success' : 'badge-warning'">{{ h.status === 'confirmed' ? $t('confirmed') : $t('draft') }}</span></td>
                <td class="text-right">
                  <button v-if="h.status !== 'confirmed'" @click.stop="deleteReconcile(h._id)" class="btn btn-sm btn-link text-danger p-0 mr-2" :title="$t('deleteBtn')" :disabled="deleting">
                    <font-awesome-icon icon="trash"/>
                  </button>
                  <font-awesome-icon icon="chevron-right" class="text-gray-999"/>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal drill-down từng tin -->
    <b-modal v-model="showDrill" :title="drillTitle" size="lg" hide-footer>
      <div class="text-center" v-if="drillLoading"><font-awesome-icon icon="spinner" spin/></div>
      <template v-else>
        <table class="table table-sm">
          <thead><tr>
            <th>{{ $t('phone') }}</th><th>{{ $t('client') }}</th><th>{{ $t('sendTime') }}</th>
            <th class="text-right">{{ $t('segments') }}</th><th class="text-right">{{ $t('fee') }}</th>
          </tr></thead>
          <tbody>
            <tr v-for="z in drillItems" :key="z._id">
              <td>{{ z.number }}</td>
              <td>{{ z.client ? z.client.companyName : '—' }}</td>
              <td>{{ z.sendTime | moment('DD/MM/YY HH:mm') }}</td>
              <td class="text-right">{{ z.numberOfSms || 1 }}</td>
              <td class="text-right">{{ z.chargedFee | numFormat('0,0') }}đ</td>
            </tr>
          </tbody>
        </table>
        <div class="d-flex align-items-center justify-content-between">
          <small class="text-gray-999">{{ $t('totalMsg', [numeralFmt(drillCount)]) }}</small>
          <b-pagination v-if="drillCount > drillPageSize" size="sm" class="mb-0"
                        v-model="drillPage" :total-rows="drillCount" :per-page="drillPageSize"
                        @input="loadDrill"></b-pagination>
        </div>
      </template>
    </b-modal>
  </main>
</template>

<script>
import Excel from 'exceljs';
import {saveAs} from 'file-saver';
import moment from 'moment';
import numeral from 'numeral';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';

export default {
  components: {DatePicker},
  data() {
    return {
      period: moment().format('YYYY-MM'),
      gateway: '',
      gateways: [],
      loading: false,
      loaded: false,
      importing: false,
      exporting: false,
      confirming: false,
      deleting: false,
      pivotLines: [],
      pivotTotals: {billableCount: 0, revenue: 0, subscriptionRevenue: 0, subscriptionCount: 0, failCount: 0, sentTotal: 0},
      saved: null,
      history: [],
      showDrill: false,
      drillLoading: false,
      drillItems: [],
      drillCount: 0,
      drillPage: 1,
      drillPageSize: 50,
      drillBrandName: null,
      drillTitle: '',
    };
  },
  computed: {
    gatewayOptions() {
      // "Tất cả" = xem tổng quan pivot DB across provider. Riêng IMPORT file bắt buộc chọn 1 provider.
      return [{value: '', text: this.$t('allProviders')}, ...this.gateways.map(g => ({value: g._id, text: g.name}))];
    },
    // Doanh thu thuê bao brand name (thu của khách) — vế đối ứng của phí duy trì trên hoá đơn NCC.
    subscriptionRevenue() {
      return this.saved ? (this.saved.systemTotals.subscriptionRevenue || 0) : 0;
    },
    totalRevenue() {
      if (!this.saved) return 0;
      return (this.saved.systemTotals.revenue || 0) + this.subscriptionRevenue;
    },
    // Giá bán chưa VAT nên so với CỘNG (trước VAT) mới cùng gốc; margin sau VAT giữ để biết dòng tiền thật.
    marginBeforeVat() {
      if (!this.saved) return 0;
      return this.totalRevenue - (this.saved.providerTotals.grossAmount || 0);
    },
    marginAfterVat() {
      if (!this.saved) return 0;
      return this.totalRevenue - (this.saved.providerTotals.payable || 0);
    },
    // Riêng khoản duy trì brand name: thu thuê bao − phí duy trì trả NCC (đều chưa VAT).
    maintainMargin() {
      if (!this.saved) return 0;
      return this.subscriptionRevenue - (this.saved.providerTotals.maintainAmount || 0);
    },
    maintainLines() {
      return (this.saved && this.saved.maintainLines) || [];
    },
    subscriptionLines() {
      return (this.saved && this.saved.subscriptionLines) || [];
    },
    unbilledBrands() {
      return (this.saved && this.saved.unbilledBrands) || [];
    },
    // Chỉ brand có phí > 0 mới là thất thu; brand miễn phí nằm chung danh sách để đối chiếu cho đủ.
    unbilledLeak() {
      return this.unbilledBrands.filter(b => b.expectedFee > 0);
    },
    unbilledLeakAmount() {
      return this.unbilledLeak.reduce((s, b) => s + (b.expectedFee || 0), 0);
    },
    maintainTotals() {
      const t = {count: 0, amount: 0};
      for (const l of this.maintainLines) {
        t.count += l.count || 0;
        t.amount += l.amount || 0;
      }
      return t;
    },
    totalDiffCount() {
      return this.saved ? this.saved.lines.reduce((s, l) => s + (l.diffCount || 0), 0) : 0;
    },
    savedTotals() {
      const t = {systemCount: 0, providerCount: 0, diffCount: 0, providerAmount: 0, systemRevenue: 0};
      if (this.saved) {
        for (const l of this.saved.lines) {
          t.systemCount += l.systemCount || 0;
          t.providerCount += l.providerCount || 0;
          t.diffCount += l.diffCount || 0;
          t.providerAmount += l.providerAmount || 0;
          t.systemRevenue += l.systemRevenue || 0;
        }
      }
      return t;
    },
  },
  mounted() {
    this.loadGateways();
    this.loadHistory();
  },
  methods: {
    numeralFmt(n) {
      return numeral(n).format('0,0');
    },
    formatPeriod(p) {
      return p ? moment(p, 'YYYY-MM').format('MM/YYYY') : '';
    },
    // Lịch sử hiển thị lợi nhuận TRƯỚC VAT (cùng gốc với giá bán chưa VAT), gồm cả thuê bao brand name.
    historyMargin(h) {
      const revenue = (h.systemTotals.revenue || 0) + (h.systemTotals.subscriptionRevenue || 0);
      return revenue - (h.providerTotals.grossAmount || 0);
    },
    async loadGateways() {
      try {
        const {data} = await this.$service.get('/sms-gateway', {params: {pageSize: 100}});
        this.gateways = (data && data.docs) || [];
      } catch (e) {
        this.gateways = [];
      }
    },
    async loadHistory() {
      try {
        const {data} = await this.$service.get('/sms-reconcile', {params: {pageSize: 24}});
        this.history = data.data || [];
      } catch (e) {
        this.history = [];
      }
    },
    async loadPivot() {
      if (!this.period) return;
      const loader = this.$loading.show();
      this.loading = true;
      try {
        const {data} = await this.$service.get('/sms-reconcile/pivot', {params: {period: this.period, gateway: this.gateway}});
        const d = data.data || {};
        this.pivotLines = d.lines || [];
        this.pivotTotals = d.totals || {billableCount: 0, revenue: 0, subscriptionRevenue: 0, subscriptionCount: 0, failCount: 0, sentTotal: 0};
        this.saved = d.saved || null;
        this.loaded = true;
      } catch (e) {
        this.$swal({text: this.errMsg(e, 'Lỗi tải báo cáo'), icon: 'error'});
      } finally {
        this.loading = false;
        loader.hide();
      }
    },
    async onFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      if (!this.gateway) {
        this.$swal({text: this.$t('selectProviderFirst'), icon: 'warning'});
        return;
      }
      const loader = this.$loading.show();
      this.importing = true;
      try {
        const buffer = await file.arrayBuffer();
        const parsed = await this.parseProviderFile(buffer);
        if (!parsed.providerLines.length && !parsed.providerTotals.maintainAmount) throw new Error(this.$t('parseNoLines'));
        const {data} = await this.$service.post('/sms-reconcile/import', {
          period: this.period,
          gateway: this.gateway,
          importedFileName: file.name,
          providerTotals: parsed.providerTotals,
          providerLines: parsed.providerLines,
          providerMaintainLines: parsed.providerMaintainLines,
        });
        this.saved = data.data;
        this.loaded = true;
        await this.loadHistory();
        this.$swal({text: this.$t('importSuccess'), icon: 'success', timer: 1500, showConfirmButton: false});
      } catch (err) {
        this.$swal({text: this.errMsg(err, 'Lỗi import file'), icon: 'error'});
      } finally {
        this.importing = false;
        loader.hide();
      }
    },
    async confirm(confirmed) {
      if (!this.saved) return;
      this.confirming = true;
      try {
        const {data} = await this.$service.patch(`/sms-reconcile/${this.saved._id}/confirm`, {confirmed});
        this.saved = data.data;
        await this.loadHistory();
      } catch (e) {
        this.$swal({text: this.errMsg(e, 'Lỗi cập nhật trạng thái'), icon: 'error'});
      } finally {
        this.confirming = false;
      }
    },
    async deleteReconcile(id) {
      const c = await this.$swal({
        title: this.$t('confirmDeleteTitle'), text: this.$t('confirmDeleteText'), icon: 'warning',
        showCancelButton: true, confirmButtonText: this.$t('deleteBtn'),
        cancelButtonText: this.$t('cancel'), confirmButtonColor: '#d33',
      });
      if (!c || !c.isConfirmed) return;
      this.deleting = true;
      try {
        await this.$service.delete(`/sms-reconcile/${id}`);
        const wasOpen = this.saved && this.saved._id === id;
        await this.loadHistory();
        if (wasOpen) await this.loadPivot(); // làm mới về pivot của provider/kỳ hiện tại
        this.$swal({text: this.$t('deleteSuccess'), icon: 'success', timer: 1200, showConfirmButton: false});
      } catch (e) {
        this.$swal({text: this.errMsg(e, 'Lỗi xoá'), icon: 'error'});
      } finally {
        this.deleting = false;
      }
    },
    openHistory(h) {
      this.period = h.period;
      this.gateway = h.gateway ? h.gateway._id : '';
      this.loadPivot();
      window.scrollTo({top: 0, behavior: 'smooth'});
    },
    // ---- Drill-down ----
    drill(line) {
      if (!this.saved || !line.brandName) return;
      this.drillBrandName = line.brandName;
      this.drillTitle = line.brandName;
      this.drillPage = 1;
      this.showDrill = true;
      this.loadDrill();
    },
    async loadDrill() {
      this.drillLoading = true;
      try {
        const {data} = await this.$service.get(`/sms-reconcile/${this.saved._id}/detail`, {
          params: {brandName: this.drillBrandName, page: this.drillPage, pageSize: this.drillPageSize},
        });
        this.drillItems = data.data || [];
        this.drillCount = data.itemCount || 0;
      } catch (e) {
        this.$swal({text: 'Lỗi tải chi tiết', icon: 'error'});
      } finally {
        this.drillLoading = false;
      }
    },
    // ---- Export ----
    async exportExcel() {
      if (!this.saved) return;
      this.exporting = true;
      try {
        const wb = new Excel.Workbook();
        const s = this.saved;
        const ws = wb.addWorksheet(this.$t('reconcileSheet'));
        ws.columns = [
          {header: this.$t('brand'), key: 'b', width: 28},
          {header: this.$t('systemCount'), key: 'sc', width: 14},
          {header: this.$t('providerCount'), key: 'pc', width: 14},
          {header: this.$t('diff'), key: 'd', width: 10},
          {header: this.$t('providerAmount'), key: 'pa', width: 16},
          {header: this.$t('systemRevenueShort'), key: 'sr', width: 16},
        ];
        ws.getRow(1).font = {bold: true};
        for (const l of s.lines) {
          ws.addRow({b: l.brandName, sc: l.systemCount, pc: l.providerCount, d: l.diffCount, pa: l.providerAmount, sr: l.systemRevenue});
        }
        const t = this.savedTotals;
        const totalRow = ws.addRow({b: this.$t('total'), sc: t.systemCount, pc: t.providerCount, d: t.diffCount, pa: t.providerAmount, sr: t.systemRevenue});
        totalRow.font = {bold: true};

        const ws2 = wb.addWorksheet(this.$t('moneyRecon'));
        ws2.columns = [{header: '', key: 'k', width: 30}, {header: '', key: 'v', width: 18}];
        const rows2 = [
          [this.$t('usageAmount'), s.providerTotals.usageAmount],
          [this.$t('maintainAmount'), s.providerTotals.maintainAmount],
          [this.$t('grossAmount'), s.providerTotals.grossAmount],
          [this.$t('vat'), s.providerTotals.vat],
          [this.$t('providerPayable'), s.providerTotals.payable],
          [this.$t('msgRevenue'), s.systemTotals.revenue],
          [this.$t('subscriptionRevenue'), this.subscriptionRevenue],
          [this.$t('totalRevenue'), this.totalRevenue],
          [this.$t('marginBeforeVat'), this.marginBeforeVat],
          [this.$t('marginAfterVat'), this.marginAfterVat],
        ];
        rows2.forEach(r => ws2.addRow({k: r[0], v: r[1]}));

        // Sheet phí duy trì: 2 phía để cạnh nhau (NCC theo mạng · Callio theo brand), khớp ở tổng.
        if (this.maintainLines.length || this.subscriptionLines.length) {
          const ws3 = wb.addWorksheet(this.$t('maintainRecon'));
          ws3.columns = [
            {header: this.$t('carrier'), key: 'c', width: 18},
            {header: this.$t('brandMonths'), key: 'q', width: 14},
            {header: this.$t('amount'), key: 'a', width: 16},
            {header: '', key: 'sp', width: 4},
            {header: this.$t('brand'), key: 'b', width: 22},
            {header: this.$t('client'), key: 'cl', width: 34},
            {header: this.$t('amount'), key: 'sa', width: 16},
          ];
          ws3.getRow(1).font = {bold: true};
          const rowCount = Math.max(this.maintainLines.length, this.subscriptionLines.length);
          for (let i = 0; i < rowCount; i++) {
            const m = this.maintainLines[i];
            const sub = this.subscriptionLines[i];
            ws3.addRow({
              c: m ? m.carrier : '', q: m ? m.count : '', a: m ? m.amount : '',
              b: sub ? sub.brandName : '', cl: sub ? sub.clientName : '', sa: sub ? sub.amount : '',
            });
          }
          const totalRow3 = ws3.addRow({
            c: this.$t('total'),
            q: this.maintainLines.length ? this.maintainTotals.count : s.providerTotals.maintainCount,
            a: this.maintainLines.length ? this.maintainTotals.amount : s.providerTotals.maintainAmount,
            b: this.$t('total'), sa: this.subscriptionRevenue,
          });
          totalRow3.font = {bold: true};
          ws3.addRow({c: this.$t('maintainMargin'), q: '', a: this.maintainMargin});
        }

        const buffer = await wb.xlsx.writeBuffer();
        const gw = s.gateway ? s.gateway.name : 'all';
        saveAs(new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
          `doi-soat-sms-${s.period}-${gw}.xlsx`);
      } catch (e) {
        this.$swal({text: 'Lỗi xuất Excel', icon: 'error'});
      } finally {
        this.exporting = false;
      }
    },
    errMsg(e, fallback) {
      return (e.response && e.response.data && e.response.data.message) || e.message || fallback;
    },
    // ---- Parse file provider Vietguys (exceljs) ----
    cellRaw(cell) {
      if (cell == null) return null;
      const v = cell.value;
      if (v == null) return null;
      if (typeof v === 'object') {
        if (v.result != null) return v.result;
        if (v.text != null) return v.text;
        if (v.richText) return v.richText.map(r => r.text).join('');
        return null;
      }
      return v;
    },
    cellNum(cell) {
      const v = this.cellRaw(cell);
      if (typeof v === 'number') return v;
      if (v == null) return 0;
      const n = parseFloat(String(v).replace(/[^\d.-]/g, ''));
      return isNaN(n) ? 0 : n;
    },
    cellStr(cell) {
      const v = this.cellRaw(cell);
      return v == null ? '' : String(v).trim();
    },
    // Bóc tên thương hiệu từ "...thương hiệu <X> tháng MM/YYYY".
    extractBrand(name) {
      const m = String(name || '').match(/thương hiệu\s+(.+?)\s+tháng/i);
      return m ? m[1].trim() : null;
    },
    // Bóc nhà mạng từ "Phí duy trì brand name mạng <X> tháng MM/YYYY" (dự phòng khi cột nhà mạng trống).
    extractCarrier(name) {
      const m = String(name || '').match(/mạng\s+(.+?)\s+tháng/i);
      return m ? m[1].trim() : null;
    },
    async parseProviderFile(buffer) {
      const wb = new Excel.Workbook();
      await wb.xlsx.load(buffer);
      // File Vietguys: 1 sheet dữ liệu (thường tên "Cost"). Chọn sheet đầu có cột nhà mạng + thành tiền.
      const ws = wb.worksheets.find(w => /cost/i.test(w.name)) || wb.worksheets[0];
      if (!ws) throw new Error(this.$t('parseNoSheet'));

      // Tìm hàng tiêu đề (chứa "nhà mạng" & "thành tiền") + map cột động.
      let headerRow = 0, col = {name: 2, carrier: 3, type: 4, qty: 6, price: 7, amount: 8};
      ws.eachRow((row, rn) => {
        if (headerRow) return;
        const cells = row.values.map(v => (v && typeof v === 'object' && v.result != null ? v.result : v));
        const joined = cells.map(c => String(c || '').toLowerCase()).join('|');
        if (joined.includes('nhà mạng') || joined.includes('nha mang') || (joined.includes('thành tiền') && joined.includes('số lượng'))) {
          headerRow = rn;
          cells.forEach((c, idx) => {
            const st = String(c || '').toLowerCase();
            if (st.includes('hàng hóa') || st.includes('hang hoa') || st.includes('dịch vụ') || st.includes('dich vu')) col.name = idx;
            else if (st.includes('nhà mạng') || st.includes('nha mang')) col.carrier = idx;
            else if (st === 'loại' || st === 'loai' || st.includes('loại')) col.type = idx;
            else if (st.includes('số lượng') || st.includes('so luong')) col.qty = idx;
            else if (st.includes('đơn giá') || st.includes('don gia')) col.price = idx;
            else if (st.includes('thành tiền') || st.includes('thanh tien')) col.amount = idx;
          });
        }
      });
      if (!headerRow) throw new Error(this.$t('parseNoSheet'));

      const usageMap = new Map(); // key normalize brand -> {brandName, count, amount}
      // Phí duy trì trong file tính theo NHÀ MẠNG ("Phí duy trì brand name mạng mobifone tháng MM/YYYY",
      // số lượng = số brand trên mạng đó), không tách theo brand → gom theo cột nhà mạng.
      const maintainMap = new Map();
      let maintainCount = 0, maintainAmount = 0, payable = 0, grossFromFile = 0, vatFromFile = 0;

      ws.eachRow((row, rn) => {
        if (rn <= headerRow) return;
        const nameStr = this.cellStr(row.getCell(col.name));
        const typeStr = this.cellStr(row.getCell(col.type));
        const label = nameStr.toLowerCase();

        // Các dòng tổng ở cuối bảng (cột "hàng hóa" trống, có nhãn CỘNG/TỔNG CỘNG).
        const rowText = row.values.map(v => String((v && typeof v === 'object' && v.result != null ? v.result : v) || '').toLowerCase()).join('|');
        if (rowText.includes('tổng cộng') || rowText.includes('tong cong')) {
          payable = this.rowLastNum(row);
          return;
        }
        if (/(^|\|)\s*cộng\s*[:|]/.test(rowText) || rowText.includes('|cộng:') || label.startsWith('cộng')) {
          grossFromFile = this.rowLastNum(row);
          return;
        }

        if (!typeStr) {
          // Hàng giữa CỘNG và TỔNG CỘNG (chỉ có số) = VAT.
          const only = this.rowLastNum(row);
          if (only && grossFromFile && !payable) vatFromFile = only;
          return;
        }

        const qty = this.cellNum(row.getCell(col.qty));
        const amount = this.cellNum(row.getCell(col.amount));
        // Bỏ qua hàng rác không có sản lượng lẫn thành tiền (footer chữ ký, ghi chú...).
        if (!qty && !amount) return;

        if (/maintain/i.test(typeStr)) {
          maintainCount += qty;
          maintainAmount += amount;
          // Cột nhà mạng trống thì bóc từ tên hàng hoá "Phí duy trì brand name mạng <X> tháng MM/YYYY".
          const carrier = this.cellStr(row.getCell(col.carrier)) || this.extractCarrier(nameStr) || this.$t('unknownCarrier');
          const ck = carrier.toLowerCase();
          const curM = maintainMap.get(ck) || {carrier, count: 0, amount: 0};
          curM.count += qty;
          curM.amount += amount;
          maintainMap.set(ck, curM);
        } else {
          // Usage: gom theo brand (nếu không bóc được tên → "(không rõ brand)").
          const brand = this.extractBrand(nameStr) || '(không rõ brand)';
          const k = brand.trim().toLowerCase();
          const cur = usageMap.get(k) || {brandName: brand, count: 0, amount: 0};
          cur.count += qty;
          cur.amount += amount;
          usageMap.set(k, cur);
        }
      });

      const providerLines = [...usageMap.values()];
      const usageCount = providerLines.reduce((s, l) => s + l.count, 0);
      const usageAmount = providerLines.reduce((s, l) => s + l.amount, 0);
      const grossAmount = grossFromFile || (usageAmount + maintainAmount);
      const vat = payable ? Math.max(0, payable - grossAmount) : (vatFromFile || Math.round(grossAmount * 0.1));
      const finalPayable = payable || (grossAmount + vat);

      return {
        providerLines,
        providerMaintainLines: [...maintainMap.values()],
        providerTotals: {
          usageCount, usageAmount, maintainCount, maintainAmount,
          grossAmount, vat, payable: finalPayable,
        },
      };
    },
    // Lấy số ở ô cuối cùng có giá trị số của hàng (dùng cho dòng tổng CỘNG/TỔNG CỘNG).
    rowLastNum(row) {
      let last = 0;
      row.eachCell({includeEmpty: false}, (cell) => {
        const n = this.cellNum(cell);
        if (n) last = n;
      });
      return last;
    },
  },
  i18n: {
    messages: {
      en: {
        pageTitle: 'SMS reconciliation',
        period: 'Period', selectMonth: 'Select month', provider: 'Provider', allProviders: 'All providers',
        loadReport: 'Load report', importProviderFile: 'Import provider file', export: 'Export',
        reconcileNote: '"All providers" shows a DB overview across gateways. To reconcile a file, pick the provider that matches it (each file belongs to one provider) — Callio then counts only SMS sent through that gateway. Billable SMS are grouped by brand name & send time (records have no carrier, so provider carriers are merged per brand). Cost/VAT/maintenance come from the provider file only.',
        pivotOnlyNote: 'DB figures only (segments + revenue) for this provider. Import the provider file to reconcile cost & margin.',
        selectProviderFirst: 'Please select a specific provider before importing (each file belongs to one provider)',
        systemRevenue: 'Callio revenue', providerPayable: 'Provider payable', grossMargin: 'Gross margin',
        qtyDiff: 'Qty diff', matched: 'Matched', needReview: 'Needs review',
        msgRevenue: 'SMS revenue', msgRevenueShort: 'SMS',
        subscriptionRevenue: 'Brand subscription revenue', subscriptionRevenueShort: 'subscription',
        totalRevenue: 'Total revenue', brandUnit: 'brands', msgUnit: 'messages',
        marginBeforeVat: 'Gross margin (before VAT)', marginAfterVat: 'Margin after VAT',
        marginAfterVatShort: 'after VAT:',
        maintainRecon: 'Brand maintenance reconciliation',
        maintainProviderSide: 'Provider charges — per carrier (each brand is billed on every registered carrier)',
        maintainSystemSide: 'Callio subscription revenue — per brand',
        maintainNoDetail: 'No per-carrier breakdown — re-import the provider file to get it',
        noSubscription: 'No brand subscription charged this period — check billing',
        maintainMargin: 'Maintenance margin (subscription − maintenance cost)',
        maintainReconNote: 'The provider bills maintenance per carrier, Callio bills subscription per brand — the two sides only reconcile at the total, not line by line.',
        carrier: 'Carrier', amount: 'Amount', unknownCarrier: '(unknown carrier)',
        providerGross: 'Provider cost (before VAT)', withVatShort: 'with VAT:',
        unbilledBrands: 'Active brands not charged this period',
        unbilledLeak: 'Uncollected', monthUnit: 'month', freeOfCharge: 'free',
        ctxUnitNote: 'Sent / charged / failed are all counted in segments (messages in brackets).',
        liveBackfillNote: 'Subscription revenue and the context counters were computed live (this period was imported before those fields existed). Re-import the file to snapshot them.',
        billableMsg: 'billable msg', brand: 'Brand name',
        systemCount: 'Callio segments', providerCount: 'Provider segments', diff: 'Diff', providerAmount: 'Provider amount',
        systemRevenueShort: 'Revenue', providerOnly: 'Provider only', systemOnly: 'Callio only',
        viewDetail: 'View messages', total: 'Total', noData: 'No data for this period',
        phone: 'Phone', client: 'Client', sendTime: 'Send time', fee: 'Fee', segments: 'Segments', totalMsg: 'Total {0} messages',
        importSuccess: 'Imported & reconciled',
        parseNoSheet: 'File missing data sheet (Cost)', parseNoLines: 'No SMS rows parsed from file',
        moneyRecon: 'Money reconciliation', usageAmount: 'SMS usage cost', maintainAmount: 'Brand maintenance',
        brandMonths: 'brand-months', grossAmount: 'Subtotal (before VAT)', vat: 'VAT',
        maintainNote: 'Brand maintenance is billed per carrier in the file (not per brand) — shown as a separate cost line, not reconciled at brand level.',
        contextTitle: 'Context', ctxSent: 'sent', ctxCharged: 'charged', ctxFail: 'failed',
        diffLegend: 'Diff (+) = segments Callio charged but the provider did not bill. Brand names are matched case-insensitively; unmatched brands appear as separate lines.',
        history: 'Reconciliation history', status: 'Status', draft: 'Draft', confirmed: 'Confirmed',
        importedBy: 'Imported by', confirmedBy: 'Confirmed by', confirmBtn: 'Confirm', unconfirmBtn: 'Unconfirm',
        deleteBtn: 'Delete', cancel: 'Cancel',
        confirmDeleteTitle: 'Delete this draft?', confirmDeleteText: 'The draft reconciliation for this period will be removed. This cannot be undone.',
        deleteSuccess: 'Deleted',
        reconcileSheet: 'Reconciliation',
      },
      vi: {
        pageTitle: 'Đối soát SMS',
        period: 'Kỳ', selectMonth: 'Chọn tháng', provider: 'Nhà cung cấp', allProviders: 'Tất cả nhà cung cấp',
        loadReport: 'Tải báo cáo', importProviderFile: 'Import file nhà cung cấp', export: 'Xuất Excel',
        reconcileNote: '"Tất cả nhà cung cấp" = xem tổng quan số liệu DB across gateway. Để đối soát file, chọn đúng nhà cung cấp của file (mỗi file thuộc 1 nhà cung cấp) — Callio chỉ đếm SMS gửi qua gateway đó. Tin tính phí gom theo tên thương hiệu & thời điểm gửi (bản ghi SMS không lưu nhà mạng nên các nhà mạng của cùng brand được gộp lại). Giá vốn/VAT/phí duy trì chỉ lấy từ file nhà cung cấp.',
        pivotOnlyNote: 'Chỉ số liệu từ DB (segment + doanh thu) của nhà cung cấp này. Import file nhà cung cấp để đối soát giá vốn & lợi nhuận.',
        selectProviderFirst: 'Vui lòng chọn 1 nhà cung cấp cụ thể trước khi import (mỗi file thuộc 1 nhà cung cấp)',
        systemRevenue: 'Doanh thu Callio', providerPayable: 'Phải trả NCC', grossMargin: 'Lợi nhuận gộp',
        qtyDiff: 'Lệch sản lượng', matched: 'Khớp', needReview: 'Cần rà soát',
        msgRevenue: 'Doanh thu tin nhắn', msgRevenueShort: 'tin',
        subscriptionRevenue: 'Doanh thu thuê bao brand name', subscriptionRevenueShort: 'thuê bao',
        totalRevenue: 'Tổng doanh thu', brandUnit: 'brand', msgUnit: 'tin',
        marginBeforeVat: 'Lợi nhuận gộp (trước VAT)', marginAfterVat: 'Lợi nhuận sau VAT',
        marginAfterVatShort: 'sau VAT:',
        maintainRecon: 'Đối soát phí duy trì brand name',
        maintainProviderSide: 'NCC tính — theo nhà mạng (mỗi brand bị tính trên từng mạng đã đăng ký)',
        maintainSystemSide: 'Callio thu thuê bao — theo brand',
        maintainNoDetail: 'Chưa có chi tiết theo nhà mạng — import lại file NCC để có',
        noSubscription: 'Kỳ này chưa thu thuê bao brand name nào — cần kiểm tra lại billing',
        maintainMargin: 'Lãi/lỗ khoản duy trì (thu thuê bao − phí duy trì)',
        maintainReconNote: 'File NCC tính phí duy trì theo nhà mạng, Callio thu thuê bao theo brand — hai phía chỉ khớp ở tổng tiền, không ghép được từng dòng.',
        carrier: 'Nhà mạng', amount: 'Thành tiền', unknownCarrier: '(không rõ nhà mạng)',
        providerGross: 'Giá vốn NCC (trước VAT)', withVatShort: 'có VAT:',
        unbilledBrands: 'Brand đang duy trì mà kỳ này không thu',
        unbilledLeak: 'Thất thu', monthUnit: 'tháng', freeOfCharge: 'miễn phí',
        ctxUnitNote: 'Đã gửi / tính phí / gửi lỗi đều tính theo segment (số trong ngoặc là số bản ghi tin).',
        liveBackfillNote: 'Doanh thu thuê bao và số liệu bối cảnh đang tính sống (kỳ này import trước khi có các trường đó). Import lại file để chốt vào bản lưu.',
        billableMsg: 'tin tính phí', brand: 'Thương hiệu',
        systemCount: 'Callio (segment)', providerCount: 'NCC (segment)', diff: 'Lệch', providerAmount: 'NCC thành tiền',
        systemRevenueShort: 'Doanh thu', providerOnly: 'Chỉ có ở NCC', systemOnly: 'Chỉ có ở Callio',
        viewDetail: 'Xem chi tiết tin', total: 'Tổng', noData: 'Không có dữ liệu trong kỳ',
        phone: 'Số điện thoại', client: 'Khách hàng', sendTime: 'Thời điểm gửi', fee: 'Phí', segments: 'Segment', totalMsg: 'Tổng {0} tin',
        importSuccess: 'Đã import & đối soát',
        parseNoSheet: 'File thiếu sheet dữ liệu (Cost)', parseNoLines: 'Không đọc được dòng SMS nào từ file',
        moneyRecon: 'Đối soát tiền', usageAmount: 'Cước tin nhắn', maintainAmount: 'Phí duy trì brand name',
        brandMonths: 'brand-tháng', grossAmount: 'Cộng (trước VAT)', vat: 'VAT',
        maintainNote: 'Phí duy trì brand name trong file tính theo nhà mạng (không tách theo brand) — hiển thị thành một dòng chi phí riêng, không đối soát theo từng brand.',
        contextTitle: 'Bối cảnh', ctxSent: 'đã gửi', ctxCharged: 'tính phí', ctxFail: 'gửi lỗi',
        diffLegend: 'Lệch (+) = segment Callio đã tính phí nhưng NCC không tính. Tên brand khớp không phân biệt hoa/thường; brand không khớp hiện thành dòng riêng.',
        history: 'Lịch sử đối soát', status: 'Trạng thái', draft: 'Nháp', confirmed: 'Đã chốt',
        importedBy: 'Người import', confirmedBy: 'Người chốt', confirmBtn: 'Chốt đối soát', unconfirmBtn: 'Bỏ chốt',
        deleteBtn: 'Xoá', cancel: 'Huỷ',
        confirmDeleteTitle: 'Xoá đối soát nháp?', confirmDeleteText: 'Bản đối soát nháp của kỳ này sẽ bị xoá, không khôi phục được.',
        deleteSuccess: 'Đã xoá',
        reconcileSheet: 'Đối soát',
      },
    },
  },
};
</script>

<style scoped>
.bg-danger-soft { background-color: rgba(220, 53, 69, 0.08); }
</style>
